// Shared spinning wheel: the teacher triggers a spin, everyone watches the
// same wheel land on the same student. Segment order is sorted by pid so all
// clients build an identical wheel from their own participant list.
// KEEP IN SYNC with the dashboard original.
import { useEffect, useRef, useState } from "react";

const COLORS = ["#0080dc", "#43a047", "#fb8c00", "#8e24aa", "#e53935", "#00897b", "#f6bf26", "#5c6bc0"];

const WheelOverlay = ({ wheel, students, doneLabel }) => {
    const canvasRef = useRef(null);
    const [phase, setPhase] = useState("spinning"); // spinning | done | hidden
    const rafRef = useRef(null);

    const names = [...students]
        .sort((a, b) => (a.pid > b.pid ? 1 : -1))
        .map((s) => ({ pid: s.pid, name: s.name }));

    useEffect(() => {
        if (!wheel) return;
        setPhase("spinning");

        const canvas = canvasRef.current;
        if (!canvas) return;
        const size = 320;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        const count = Math.max(names.length, 1);
        const seg = (Math.PI * 2) / count;
        const targetIndex = Math.max(0, names.findIndex((n) => n.pid === wheel.pid));

        // land the middle of the target segment under the top pointer (-90°),
        // after a few full turns
        const finalRotation =
            Math.PI * 2 * 5 + (-Math.PI / 2) - (targetIndex * seg + seg / 2);

        const draw = (rotation) => {
            ctx.clearRect(0, 0, size, size);
            ctx.save();
            ctx.translate(size / 2, size / 2);
            ctx.rotate(rotation);
            for (let i = 0; i < count; i++) {
                ctx.beginPath();
                ctx.moveTo(0, 0);
                ctx.arc(0, 0, size / 2 - 6, i * seg, (i + 1) * seg);
                ctx.closePath();
                ctx.fillStyle = COLORS[i % COLORS.length];
                ctx.fill();
                ctx.save();
                ctx.rotate(i * seg + seg / 2);
                ctx.textAlign = "right";
                ctx.fillStyle = "#fff";
                ctx.font = `bold ${count > 12 ? 11 : 14}px sans-serif`;
                const label = (names[i] ? names[i].name : "").slice(0, 14);
                ctx.fillText(label, size / 2 - 18, 5);
                ctx.restore();
            }
            ctx.restore();
            // hub
            ctx.beginPath();
            ctx.arc(size / 2, size / 2, 26, 0, Math.PI * 2);
            ctx.fillStyle = "#17202a";
            ctx.fill();
        };

        const start = performance.now();
        const duration = wheel.durationMs || 4000;
        const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
            draw(finalRotation * eased);
            if (progress < 1) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                setPhase("done");
            }
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wheel && wheel.at]);

    // auto-hide a few seconds after landing
    useEffect(() => {
        if (phase !== "done") return;
        const timeout = setTimeout(() => setPhase("hidden"), 4500);
        return () => clearTimeout(timeout);
    }, [phase]);

    if (!wheel || phase === "hidden") return null;

    return (
        <div
            style={{
                position: "absolute",
                inset: 0,
                zIndex: 8,
                background: "rgba(10,16,22,0.55)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 14,
            }}
        >
            <div style={{ position: "relative" }}>
                <div
                    style={{
                        position: "absolute",
                        top: -6,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 0,
                        height: 0,
                        borderLeft: "12px solid transparent",
                        borderRight: "12px solid transparent",
                        borderTop: "20px solid #f6bf26",
                        zIndex: 2,
                    }}
                />
                <canvas ref={canvasRef} style={{ display: "block", filter: "drop-shadow(0 6px 24px rgba(0,0,0,0.4))" }} />
            </div>
            {phase === "done" && (
                <div
                    style={{
                        background: "#fff",
                        color: "#17202a",
                        borderRadius: 14,
                        padding: "10px 28px",
                        fontWeight: 800,
                        fontSize: "1.4rem",
                        boxShadow: "0 6px 30px rgba(0,0,0,0.35)",
                    }}
                >
                    🎉 {doneLabel} {wheel.name}
                </div>
            )}
        </div>
    );
};

export default WheelOverlay;
